import { observable, action, computed, makeObservable } from 'mobx';
import type { ChatMessage, ChatMessageObjectCustom } from '@/types/meeting';
import * as API from '@/api';
import { Chatroom } from '@/utils/NIM';
import { user, meeting } from './index';

function dealMsg(msg: ChatMessage) {
  const re = { ...msg };
  if (msg.custom && typeof msg.custom === 'string') {
    try {
      const data: ChatMessageObjectCustom = JSON.parse(msg.custom);
      re.custom = data;
      re.custom.nickname && (re.fromNick = re.custom.nickname);
      re.custom.avatarUrl && (re.fromAvatar = re.custom.avatarUrl);
    } catch (e) {
      console.warn(msg.custom, e);
    }
  }
  return re;
}

class Chat {
  constructor() {
    makeObservable(this);
  }

  @observable chatVisible = false;

  @observable messageList: string[] = [];

  @observable noMore = false;

  @observable loading = false;

  @observable messageMap: Map<string, ChatMessage> = new Map();

  @observable chatroom: any = null;

  @observable unReadMsg: null | ChatMessage = null;

  @observable hasNewMsg = false;

  @observable isAbleUse = true;

  @observable firstUnreadMsg: null | ChatMessage = null;

  @observable disconnected = false;

  @observable reconnecting = false;

  @observable reconnected = false;

  @action
  async chatInit(meetingId: string) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    const res = await API.getChatInfo(meetingId, String(meeting.info?.channelId));
    const { appKey, chatRoomId, chatRoomAddresses, imToken, accid } = res.data!;
    return new Promise<void>((resolve, reject) => {
      if (res.status !== 1) {
        resolve();
      }
      const chatroom = Chatroom.getInstance({
        appKey,
        account: accid,
        token: imToken,
        chatroomId: chatRoomId,
        chatroomAddresses: chatRoomAddresses,
        chatroomAvatar: user.avatarUrl,
        reconnectionAttempts: 3,
        onconnect: (obj: any) => {
          resolve(obj);
          that.disconnected = false;
          if (that.reconnecting) {
            that.reconnecting = false;
            that.reconnected = true;
          }
          console.log('链接成功', obj);
        },
        onerror(error: any, obj: any) {
          that.reconnecting = true;
          that.reconnected = false;
          reject(error);
          console.log('发生错误', error, obj);
        },
        onwillreconnect(obj: any) {
          // 此时说明 `SDK` 已经断开连接, 请开发者在界面上提示用户连接已断开, 而且正在重新建立连接
          that.reconnecting = true;
          that.reconnected = false;
          console.log('即将重连', obj);
        },
        ondisconnect(error: {
          cmd: string;
          code: number | string;
          message: string;
          retryCount: number;
          timetag: number;
          willReconnect: boolean;
        }) {
          // 此时说明 `SDK` 处于断开状态, 开发者此时应该根据错误码提示相应的错误信息, 并且跳转到登录页面
          console.log('连接断开', error);
          that.disconnected = true;
          that.reconnecting = true;
          that.reconnected = false;
          if (error) {
            switch (error.code) {
              // 账号或者密码错误, 请跳转到登录页面并提示错误
              case 302:
                break;
              // 被踢, 请提示错误后跳转到登录页面
              case 'kicked':
                break;
              default:
                break;
            }
          }
          if (!error.willReconnect) {
            reject(error);
          }
        },
        // 消息
        onmsgs: (msgList: []) => {
          const msgs = msgList.filter((n: any) => n.type !== 'notification').map(dealMsg);
          if (msgs.length) {
            this.addMessageListDown(msgs);
            if (!this.chatVisible && msgs.length) {
              this.unReadMsg = msgs[msgs.length - 1];
              if (!this.firstUnreadMsg) {
                // eslint-disable-next-line prefer-destructuring
                this.firstUnreadMsg = msgs[0];
              }
            }
          }
        },
      });
      chatroom.connect();
      this.chatroom = chatroom;
    });
  }

  @action
  setNewMsg(has: boolean) {
    this.hasNewMsg = has;
  }

  @action
  setChatVisible(visible: boolean, locateMsg?: boolean) {
    if (locateMsg) {
      this.firstUnreadMsg = null;
    } else {
      this.unReadMsg = null;
    }
    this.chatVisible = visible;
    setTimeout(() => {
      if (this.chatVisible) {
        this.unReadMsg = null;
        this.firstUnreadMsg = null;
      }
    });
  }

  @action
  sendText(text: string) {
    if (!text) return Promise.reject(new Error('参数不能为空'));
    return new Promise((resolve, reject) => {
      const sendmsg = this.chatroom.sendText({
        text,
        custom: JSON.stringify({
          nickname: user.name,
          uid: user.uid,
          yxUid: String(user.yxuid),
          avatarUrl: user.avatarUrl,
        }),
        done: (err: any, msg: ChatMessage) => {
          if (err) {
            this.updateMsg(Object.assign(sendmsg, { status: 'fail' }));
            reject(err);
          } else {
            resolve(msg);
            this.updateMsg(msg);
          }
        },
      });
      this.addMessageListDown([dealMsg(sendmsg)]);
    });
  }

  @action
  updateMsg(msg: ChatMessage) {
    this.messageMap.set(msg.idClient, dealMsg(msg));
  }

  @action
  getHistoryMessage(time: number, limit = 30): Promise<any> {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    let timetag: number;
    if (time) {
      timetag = time;
    } else if (this.messageList.length) {
      timetag = this.messageMap.get(this.messageList[0])?.time || new Date().getTime();
    } else {
      timetag = new Date().getTime();
    }
    this.loading = true;
    return new Promise((resolve, reject) => {
      this.chatroom.getHistoryMsgs({
        timetag,
        limit,
        msgTypes: ['text'],
        done: (err: any, obj: any) => {
          if (err) {
            reject(err);
            that.loading = false;
          } else {
            if (obj?.msgs?.length) {
              that.addMessageListUp(obj.msgs.map(dealMsg));
            }
            if (obj?.msgs?.length < limit) {
              that.noMore = true;
            }
            resolve(obj);
            setTimeout(() => {
              that.loading = false;
            }, 200);
          }
        },
      });
    });
  }

  @action
  resetHistoryMessage(): Promise<any> {
    const that = this;
    this.loading = true;
    return new Promise((resolve, reject) => {
      this.chatroom.getHistoryMsgs({
        timetag: new Date().valueOf(),
        limit: 30,
        msgTypes: ['text'],
        done: (err: any, obj: any) => {
          if (err) {
            that.loading = false;
            reject(err);
          } else {
            this.messageList = [];
            if (obj?.msgs?.length) {
              that.addMessageListUp(obj.msgs.map(dealMsg));
            }
            if (obj?.msgs?.length < 30) {
              that.noMore = true;
            }
            setTimeout(() => {
              that.loading = false;
            }, 200);
            resolve(obj);
          }
        },
      });
    });
  }

  @action
  addMessageListDown(msgList: ChatMessage[]) {
    msgList.forEach((msg) => this.messageMap.set(msg.idClient, msg));
    this.messageList = this.messageList.concat(
      msgList
        .sort((a, b) => {
          return a.time - b.time;
        })
        .map((msg) => msg.idClient)
    );
  }

  @action
  addMessageListUp(msgList: ChatMessage[]) {
    msgList.forEach((msg) => this.messageMap.set(msg.idClient, msg));
    this.messageList = msgList
      .sort((a, b) => {
        return a.time - b.time;
      })
      .map((msg) => msg.idClient)
      .concat(this.messageList);
  }

  // 设置聊天室是否可用
  @action
  setIsAbleUse(able: boolean) {
    if (able) {
      this.chatroom?.connect();
      this.isAbleUse = able;
    } else {
      this.isAbleUse = able;
      this.chatVisible = false;
      this.chatroom?.disconnect();
    }
  }

  @computed
  get getMessageList() {
    return this.messageList
      .map((msdId) => {
        return this.messageMap.get(msdId);
      })
      .filter((n) => !!n);
  }

  @computed
  get lastMsgId() {
    return this.messageList[this.messageList.length - 1];
  }
}

export default Chat;
